import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Purchases as PurchasesWeb } from '@revenuecat/purchases-js';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

// RevenueCat API Keys - REPLACE with your actual keys from RevenueCat dashboard
// All platforms use the same RevenueCat project, so subscriptions sync automatically
const API_KEYS = {
  // Web (RevenueCat Billing) - get from RevenueCat > Project Settings > API Keys > Web
  web: 'rcb_ABQmGtLpEWJBuhfeurEXkxdEuaCt',
  // iOS - get from RevenueCat > Project Settings > API Keys > iOS
  ios: 'appl_placeholder_key',
  // Android - get from RevenueCat > Project Settings > API Keys > Android
  android: 'goog_placeholder_key',
};

// Platform detection
const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform(); // 'web', 'ios', or 'android'

export const SubscriptionProvider = ({ children, userId }) => {
  const [isPro, setIsPro] = useState(false);
  const [currentCustomerInfo, setCurrentCustomerInfo] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Store SDK instances using refs to avoid re-render loops
  const purchasesWebRef = useRef(null);
  const purchasesNativeRef = useRef(null);

  // Track initialization to prevent infinite loops
  const initializedRef = useRef(false);

  // Handle customer info updates (works for both SDKs)
  const handleCustomerInfoUpdate = useCallback((info) => {
    if (!info) return;
    setCurrentCustomerInfo(info);

    // Check if "pro" entitlement is active
    // Both SDKs use the same entitlement structure
    const entitlements = info.entitlements?.active || info.entitlements || {};
    const isProActive = typeof entitlements['pro'] !== 'undefined';
    setIsPro(isProActive);
  }, []);

  // Initialize RevenueCat based on platform
  useEffect(() => {
    // Prevent multiple initializations
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    const initRevenueCat = async () => {
      try {
        setLoading(true);
        setError(null);

        if (isNative) {
          // ========== NATIVE (iOS/Android) ==========
          const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');

          await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

          const apiKey = platform === 'ios' ? API_KEYS.ios : API_KEYS.android;

          // Configure with user ID for cross-platform sync
          const config = { apiKey };
          if (userId) {
            config.appUserID = userId;
          }
          await Purchases.configure(config);

          purchasesNativeRef.current = Purchases;

          // Get initial customer info
          const { customerInfo } = await Purchases.getCustomerInfo();
          handleCustomerInfoUpdate(customerInfo);

          // Load offerings
          const offerings = await Purchases.getOfferings();
          if (offerings.current?.availablePackages?.length > 0) {
            setPackages(offerings.current.availablePackages);
          }

          // Listen for updates
          Purchases.addCustomerInfoUpdateListener((info) => {
            handleCustomerInfoUpdate(info.customerInfo);
          });

        } else {
          // ========== WEB (RevenueCat Billing) ==========
          // Generate anonymous user ID if not logged in
          let appUserId = userId;
          if (!appUserId) {
            const storedAnonId = localStorage.getItem('rc_anon_user_id');
            if (storedAnonId) {
              appUserId = storedAnonId;
            } else {
              appUserId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
              localStorage.setItem('rc_anon_user_id', appUserId);
            }
          }

          const instance = PurchasesWeb.configure({
            apiKey: API_KEYS.web,
            appUserId: appUserId,
          });

          purchasesWebRef.current = instance;

          // Get initial customer info
          try {
            const customerInfo = await instance.getCustomerInfo();
            handleCustomerInfoUpdate(customerInfo);
          } catch (err) {
            // User might not exist yet in RevenueCat - that's OK
            console.log('No existing customer info (new user)');
          }

          // Load offerings
          try {
            const offerings = await instance.getOfferings();
            if (offerings.current?.availablePackages?.length > 0) {
              setPackages(offerings.current.availablePackages);
            }
          } catch (err) {
            console.error('Error loading offerings:', err);
          }
        }
      } catch (err) {
        console.error('Error initializing RevenueCat:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initRevenueCat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - user login/logout handled by loginUser/logoutUser

  // Purchase a package
  const purchasePackage = useCallback(async (pack) => {
    try {
      setError(null);

      if (isNative && purchasesNativeRef.current) {
        // Native purchase flow
        const { customerInfo } = await purchasesNativeRef.current.purchasePackage({ aPackage: pack });
        handleCustomerInfoUpdate(customerInfo);
        return true;

      } else if (purchasesWebRef.current) {
        // Web purchase flow - opens RevenueCat checkout (Stripe-powered)
        const { customerInfo } = await purchasesWebRef.current.purchase({ rcPackage: pack });
        handleCustomerInfoUpdate(customerInfo);
        return true;
      }

      return false;
    } catch (err) {
      // User cancelled is not an error
      if (err.userCancelled || err.code === 'USER_CANCELLED') {
        return false;
      }
      console.error('Error purchasing:', err);
      setError(err.message);
      throw err;
    }
  }, [handleCustomerInfoUpdate]);

  // Restore purchases
  const restorePurchases = useCallback(async () => {
    try {
      setError(null);

      if (isNative && purchasesNativeRef.current) {
        const { customerInfo } = await purchasesNativeRef.current.restorePurchases();
        handleCustomerInfoUpdate(customerInfo);
        return customerInfo;

      } else if (purchasesWebRef.current) {
        // On web, just refresh customer info
        const customerInfo = await purchasesWebRef.current.getCustomerInfo();
        handleCustomerInfoUpdate(customerInfo);
        return customerInfo;
      }

      return null;
    } catch (err) {
      console.error('Error restoring purchases:', err);
      setError(err.message);
      throw err;
    }
  }, [handleCustomerInfoUpdate]);

  // Log in user (for cross-platform sync)
  const loginUser = useCallback(async (newUserId) => {
    try {
      if (isNative && purchasesNativeRef.current) {
        const { customerInfo } = await purchasesNativeRef.current.logIn({ appUserID: newUserId });
        handleCustomerInfoUpdate(customerInfo);
      } else if (purchasesWebRef.current) {
        // Web SDK: need to reconfigure with new user ID
        const instance = PurchasesWeb.configure({
          apiKey: API_KEYS.web,
          appUserId: newUserId,
        });
        purchasesWebRef.current = instance;
        const customerInfo = await instance.getCustomerInfo();
        handleCustomerInfoUpdate(customerInfo);
      }
    } catch (err) {
      console.error('Error logging in user:', err);
    }
  }, [handleCustomerInfoUpdate]);

  // Log out user
  const logoutUser = useCallback(async () => {
    try {
      if (isNative && purchasesNativeRef.current) {
        const { customerInfo } = await purchasesNativeRef.current.logOut();
        handleCustomerInfoUpdate(customerInfo);
      } else if (purchasesWebRef.current) {
        // Web SDK: reconfigure with new anonymous user ID
        localStorage.removeItem('rc_anon_user_id');
        const newAnonId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
        localStorage.setItem('rc_anon_user_id', newAnonId);

        const instance = PurchasesWeb.configure({
          apiKey: API_KEYS.web,
          appUserId: newAnonId,
        });
        purchasesWebRef.current = instance;
        setIsPro(false);
        setCurrentCustomerInfo(null);
      }
    } catch (err) {
      console.error('Error logging out user:', err);
    }
  }, [handleCustomerInfoUpdate]);

  const value = {
    // State
    isPro,
    currentCustomerInfo,
    packages,
    loading,
    error,
    platform, // 'web', 'ios', or 'android'
    isNative,

    // Actions
    purchasePackage,
    restorePurchases,
    loginUser,
    logoutUser,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
